import { Alert, Button, Icon, TextArea } from '@pms-digital/design-system';

export interface RegenPromptEditorProps {
  isRevealed: boolean;
  onToggleReveal: () => void;
  /** The editable half — scope prompt, context, coherence context, comment section. */
  body: string;
  onBodyChange: (body: string) => void;
  /** The JSON output contract, rendered read-only. Empty until the first fetch lands. */
  instructions: string;
  scopePromptName: string | null;
  scopePromptVersion: number | null;
  isFetching: boolean;
  fetchError: string | null;
  /** True while the operator has diverged `body` from the last composition. */
  isManuallyEdited: boolean;
  /** Set when the grid selection changed under a hand-edited prompt — the operator has to choose. */
  pendingConflict: boolean;
  onKeepManualEdit: () => void;
  onRecompose: () => void;
  /** Editing is blocked while a batch is in flight, like the Commentaire field beside it. */
  disabled: boolean;
}

/**
 * The regeneration prompt, revealed and editable in the drawer (issue #6) —
 * the counterpart of the wizard's récapitulatif reveal, for the six
 * regeneration scopes.
 *
 * Two decisions shape it:
 *
 * **The whole prompt is shown, in order, and the tail is locked in place.**
 * Not the body alone with a note saying instructions will be appended: the
 * operator is being asked to reason about what the model reads, and a prompt
 * with an invisible ending is one they cannot reason about. So the JSON output
 * contract sits exactly where it sits in the real prompt, greyed and
 * padlocked, and it is submitted verbatim with whatever the operator wrote
 * above it. The server does not re-append it — what runs is what was on
 * screen.
 *
 * **Nothing is decided on the operator's behalf.** A prompt they edited is
 * never silently recomposed when the grid selection moves; they are asked, and
 * the work sits untouched until they answer. Same rule, same wording, as
 * `recapitulatif-step.tsx`'s `promptManuallyEdited` flow.
 *
 * Rendered for `pms_admin` only — the caller decides, and
 * `POST /matrices/:id/composed-prompt` enforces the same gate server-side.
 */
export function RegenPromptEditor({
  isRevealed,
  onToggleReveal,
  body,
  onBodyChange,
  instructions,
  scopePromptName,
  scopePromptVersion,
  isFetching,
  fetchError,
  isManuallyEdited,
  pendingConflict,
  onKeepManualEdit,
  onRecompose,
  disabled,
}: RegenPromptEditorProps) {
  return (
    <div className="space-y-3">
      <Button type="button" variant="primary" size="sm" onClick={onToggleReveal}>
        {isRevealed ? (
          <Icon name="visibility_off" size={16} />
        ) : (
          <Icon name="visibility" size={16} />
        )}
        {isRevealed ? 'Masquer le prompt' : 'Afficher le prompt'}
      </Button>

      {isRevealed && (
        <div className="space-y-2" data-testid="regen-prompt-panel">
          {pendingConflict && (
            <div className="space-y-3 rounded-md border border-warning bg-warning-tint p-3">
              <p className="text-small">
                Vous avez modifié le prompt manuellement. Conserver votre édition ou recomposer ?
              </p>
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="primary" onClick={onKeepManualEdit}>
                  Conserver mon édition
                </Button>
                <Button variant="secondary" type="button" size="sm" onClick={onRecompose}>
                  Recomposer
                </Button>
              </div>
            </div>
          )}

          {isFetching && <p className="text-gray-500 text-small">Composition du prompt…</p>}
          {fetchError && <Alert tone="danger">{fetchError}</Alert>}

          {!isFetching && !fetchError && (
            <>
              {scopePromptName && (
                <p className="text-caption text-gray-500">
                  Prompt de scope&nbsp;:{' '}
                  <span className="font-mono">
                    {scopePromptName}
                    {scopePromptVersion === null ? '' : ` v${scopePromptVersion}`}
                  </span>
                </p>
              )}

              <TextArea
                label={<span>Prompt de régénération</span>}
                data-testid="regen-prompt-body"
                value={body}
                onChange={(event) => onBodyChange(event.target.value)}
                disabled={disabled}
              />

              {/* The tail, in place. Rendered as a <pre> rather than a
                  disabled textarea so it reads as part of the same document
                  rather than as a second, broken input. */}
              <div
                data-testid="regen-prompt-instructions"
                className="rounded-md border border-gray-300 bg-gray-100"
              >
                <p className="flex items-center gap-1 border-gray-300 border-b px-2 py-1 font-semibold text-caption text-gray-600">
                  <Icon name="lock" size={14} />
                  Contrat de sortie — non modifiable, envoyé tel quel
                </p>
                <pre className="max-h-40 overflow-auto whitespace-pre-wrap px-2 py-1 font-mono text-caption text-gray-600 leading-relaxed">
                  {instructions}
                </pre>
              </div>

              {isManuallyEdited && !pendingConflict && (
                <p className="text-caption text-gray-500">
                  Prompt édité manuellement — c'est cette version qui sera envoyée.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
